<template>
  <div class="app-container">
    <el-header height="60px" class="header">
      <div class="logo">
        <h1>Travelbolt CMS</h1>
      </div>
      <div class="user-info" v-if="isLoggedIn">
        <el-dropdown @command="handleLanguageChange" class="language-selector">
          <span class="el-dropdown-link">
            {{ currentLanguage }}
            <i class="el-icon-arrow-down"></i>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="zh-cn">简体中文</el-dropdown-item>
              <el-dropdown-item command="zh-tw">繁體中文</el-dropdown-item>
              <el-dropdown-item command="en">English</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <span class="username">{{ username }}</span>
        <el-button type="primary" plain @click="logout" class="logout-button">
          <i class="fa fa-sign-out mr-2"></i>{{ t('header.logout') }}
        </el-button>
      </div>
    </el-header>
    <el-container v-if="isLoggedIn">
      <el-aside width="220px" class="sidebar">
        <el-menu :default-active="activeMenu" class="sidebar-menu" background-color="#f8f9fa" text-color="#495057" active-text-color="#409EFF" @select="handleMenuSelect">
          <!-- 目的地管理 -->
          <el-sub-menu index="destinations-group">
            <template #title>
              <i class="fa fa-map-marker"></i>
              <span>{{ t('menu.destinations') }}</span>
            </template>
            <el-menu-item index="destinations">
              <span>{{ t('menu.destinations') }}</span>
            </el-menu-item>
            <el-menu-item index="destination-details">
              <span>{{ t('menu.destinationDetails') }}</span>
            </el-menu-item>
          </el-sub-menu>
          
          <!-- 航班优惠管理 -->
          <el-sub-menu index="flight-deals-group">
            <template #title>
              <i class="fa fa-plane"></i>
              <span>{{ t('menu.flightDeals') }}</span>
            </template>
            <el-menu-item index="flight-deals">
              <span>{{ t('menu.flightDeals') }}</span>
            </el-menu-item>
            <el-menu-item index="flight-deal-details">
              <span>{{ t('menu.flightDealDetails') }}</span>
            </el-menu-item>
          </el-sub-menu>
          
          <!-- 热门优惠管理 -->
          <el-sub-menu index="hot-deals-group">
            <template #title>
              <i class="fa fa-percent"></i>
              <span>{{ t('menu.hotDeals') }}</span>
            </template>
            <el-menu-item index="hot-deals">
              <span>{{ t('menu.hotDeals') }}</span>
            </el-menu-item>
            <el-menu-item index="hot-deal-details">
              <span>{{ t('menu.hotDealDetails') }}</span>
            </el-menu-item>
          </el-sub-menu>
          
          <!-- 内容管理 -->
          <el-sub-menu index="content-group">
            <template #title>
              <i class="fa fa-file-text"></i>
              <span>{{ t('menu.contentManagement') }}</span>
            </template>
            <el-menu-item index="services">
              <span>{{ t('menu.services') }}</span>
            </el-menu-item>
            <el-menu-item index="flash-sale">
              <span>{{ t('menu.flashSale') }}</span>
            </el-menu-item>
            <el-menu-item index="testimonials">
              <span>{{ t('menu.testimonials') }}</span>
            </el-menu-item>
            <el-menu-item index="exclusive-travel-insurance">
              <span>{{ t('menu.exclusiveTravelInsurance') }}</span>
            </el-menu-item>
            <el-menu-item index="hero-background">
              <span>{{ t('menu.heroBackground') }}</span>
            </el-menu-item>
            <el-menu-item index="footer">
              <span>{{ t('menu.footer') }}</span>
            </el-menu-item>
          </el-sub-menu>
          
          <!-- 营销管理 -->
          <el-sub-menu index="marketing-group">
            <template #title>
              <i class="fa fa-bullhorn"></i>
              <span>{{ t('menu.marketing') }}</span>
            </template>
            <el-menu-item index="exclusive-deals">
              <span>{{ t('menu.exclusiveDeals') }}</span>
            </el-menu-item>
            <el-menu-item index="subscribers">
              <span>{{ t('menu.subscribers') }}</span>
            </el-menu-item>
            <el-menu-item index="faqs">
              <span>{{ t('menu.faqs') }}</span>
            </el-menu-item>
            <el-menu-item index="about-us">
              <span>{{ t('menu.aboutUs') }}</span>
            </el-menu-item>
            <el-menu-item index="privacy-policy">
              <span>隐私政策</span>
            </el-menu-item>
            <el-menu-item index="terms-and-conditions">
              <span>条款与条件</span>
            </el-menu-item>
          </el-sub-menu>
          
          <!-- 系统管理 -->
          <el-sub-menu index="system-group">
            <template #title>
              <i class="fa fa-cog"></i>
              <span>{{ t('menu.systemManagement') }}</span>
            </template>
            <el-menu-item index="users">
              <span>{{ t('menu.users') }}</span>
            </el-menu-item>
            <el-menu-item index="languages">
              <span>{{ t('menu.languages') }}</span>
            </el-menu-item>
          </el-sub-menu>
        </el-menu>
      </el-aside>
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
    <div v-else class="login-container">
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const isLoggedIn = ref(false)
const username = ref('Admin')
const { locale, t } = useI18n()

const activeMenu = computed(() => route.name || 'destinations')

const currentLanguage = computed(() => {
  const langMap = {
    'zh-cn': '简体中文',
    'zh-tw': '繁體中文',
    'en': 'English'
  }
  return langMap[locale.value] || '简体中文'
})

const checkLoginStatus = () => {
  const token = localStorage.getItem('token')
  isLoggedIn.value = !!token
}

const logout = () => {
  localStorage.removeItem('token')
  isLoggedIn.value = false
  ElMessage.success('Logged out successfully')
  router.push('/login')
}

const handleMenuSelect = (key, keyPath) => {
  router.push(`/${key}`)
}

const handleLanguageChange = (lang) => {
  locale.value = lang
  localStorage.setItem('locale', lang)
}

// 监听路由变化，自动更新登录状态
watch(
  () => route.path,
  () => {
    checkLoginStatus()
  }
)

onMounted(() => {
  checkLoginStatus()
})
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  overflow: hidden;
  height: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}
</style>

<style scoped>
.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  background-color: #ffffff;
  color: #495057;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #e9ecef;
  z-index: 100;
}

.logo h1 {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  color: #409EFF;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.language-selector {
  margin-right: 10px;
}

.el-dropdown-link {
  cursor: pointer;
  color: #495057;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background-color: #ffffff;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.el-dropdown-link:hover {
  color: #409EFF;
  border-color: #409EFF;
  background-color: #f0f9ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.username {
  font-size: 16px;
  font-weight: 500;
  color: #495057;
}

.logout-button {
  border-color: #409EFF;
  color: #409EFF;
  font-weight: 500;
}

.logout-button:hover {
  background-color: #409EFF;
  color: white;
}

.el-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.sidebar {
  background-color: #ffffff;
  border-right: 1px solid #e9ecef;
  width: 220px;
  flex-shrink: 0;
  overflow-y: auto;
  height: calc(100vh - 60px);
  position: sticky;
  top: 60px;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
}

.sidebar-menu {
  height: 100%;
  border-right: none;
  padding: 20px 0;
  background-color: #ffffff;
}

.sidebar-menu .el-menu-item {
  height: 50px;
  line-height: 50px;
  margin: 5px 15px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.sidebar-menu .el-menu-item:hover {
  background-color: #f0f9ff;
  color: #409EFF;
}

.sidebar-menu .el-menu-item.is-active {
  background-color: #e3f2fd;
  color: #409EFF;
  font-weight: 600;
}

.sidebar-menu .el-menu-item i {
  margin-right: 12px;
  font-size: 16px;
}

.sidebar-menu .el-sub-menu i {
  margin-right: 12px;
  font-size: 16px;
}

.main-content {
  padding: 30px;
  overflow-y: auto;
  background-color: #f8f9fa;
  flex: 1;
  height: calc(100vh - 60px);
}

.login-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>